import { createMutation } from '@tanstack/svelte-query'
import { get } from 'svelte/store'
import { storeScene } from '../../../stores/scene'
import { ThreeItemKeys } from '../../../constants/three'
import { parsePositioningFile } from '../../../helpers/positioning-file'
import { add, subtract, Vector2D, zEulerRotation } from '../../../helpers/vector-2d'
import { convertToUserUnit } from '../../../helpers/scene-json'
import queryClient from '../../../constants/query-client'

type Coordinate = { x: number, y: number }

const MAX_FITNESS = 300
const COLLISION_TOLERANCE = 1
const TAU = Math.PI * 2
const RAD2DEG = 180 / Math.PI

function computeFitness(
  target: Vector2D[],
  source: Vector2D[],
  tr: Vector2D,
  rotation: number,
  minFitness: number,
): { improved: boolean, fitness: number, iteration: number } {
  let totalFitness = 0
  let iteration = 0

  for (const sPoint of source) {
    let localFitness = Infinity

    const trPoint = add(zEulerRotation(sPoint, rotation), tr)

    for (const tPoint of target) {
      iteration++
      localFitness = Math.min(localFitness, subtract(trPoint, tPoint).length())
    }

    totalFitness += localFitness

    if (totalFitness >= minFitness) {
      return { improved: false, fitness: totalFitness, iteration }
    }
  }

  return { improved: true, fitness: totalFitness, iteration }
}

function ICP(sourcePoints: Vector2D[], targetPoints: Vector2D[]) {
  if (sourcePoints.length < 2 || targetPoints.length < 2) {
    console.error('too few points to do ICP')
    return false
  }

  const sourceIsSmaller = sourcePoints.length <= targetPoints.length
  const smallList = sourceIsSmaller ? sourcePoints : targetPoints
  const largeList = sourceIsSmaller ? targetPoints : sourcePoints

  let iteration = 0
  let rotation: number
  let bestRotation = 0
  let translation: Vector2D
  let bestTranslation = new Vector2D(0, 0)
  let minFitness = Infinity

  for (const pivot of largeList) {
    for (const tPoint of largeList) {
      if (pivot === tPoint) continue

      const n1 = subtract(tPoint, pivot).normalize()
      const theta1 = n1.y > 0 ? Math.acos(n1.x) : TAU - Math.acos(n1.x)

      for (const sPoint of smallList) {
        for (const sPoint2 of smallList) {
          if (sPoint === sPoint2) continue

          const n2 = subtract(sPoint2, sPoint).normalize()
          const theta2 = n2.y > 0 ? Math.acos(n2.x) : TAU - Math.acos(n2.x)

          rotation = theta2 - theta1
          translation = subtract(pivot, zEulerRotation(sPoint, rotation))

          const res = computeFitness(largeList, smallList, translation, rotation, minFitness)
          iteration += res.iteration

          if (res.improved) {
            minFitness = res.fitness

            if (!sourceIsSmaller) {
              bestRotation = -rotation
              bestTranslation = subtract(sPoint2, zEulerRotation(tPoint, bestRotation))
            }
            else {
              bestRotation = rotation
              bestTranslation = translation
            }

            if (minFitness < COLLISION_TOLERANCE) {
              return { rotation: RAD2DEG * bestRotation, translation: bestTranslation, iteration }
            }
          }
        }
      }
    }
  }

  if (minFitness > MAX_FITNESS * smallList.length) return false

  return { rotation: RAD2DEG * bestRotation, translation: bestTranslation, iteration }
}

const usePositioningAssistant = () => {
  const checkPositionsComplianceMutation = createMutation(() => ({
    mutationFn: async ({ printerUuid, fileUuid }: { printerUuid: string, fileUuid: string }) => {
      const scene = get(storeScene)

      const foundFile = scene?.getObjectByProperty('uuid', fileUuid)
      if (!foundFile) throw new Error('file not found')

      const foundPrinter = scene?.getObjectByProperty('uuid', printerUuid)
      if (!foundPrinter) throw new Error('Printer not found')

      const fileCoordinates = JSON.parse(foundFile.userData[ThreeItemKeys.ICP_COORDINATES])
      const printerCoordinates = JSON.parse(foundPrinter.userData[ThreeItemKeys.ICP_COORDINATES])

      if (!fileCoordinates || !printerCoordinates) throw new Error('Missing coordinates')

      const icpResult = ICP(
        printerCoordinates.map((coordinate: Coordinate) =>
          new Vector2D(
            coordinate.x,
            coordinate.y,
          ),
        ),
        fileCoordinates.map((coordinate: Coordinate) =>
          new Vector2D(
            coordinate.x + (convertToUserUnit(foundFile.position.x) as number),
            coordinate.y + (convertToUserUnit(foundFile.position.y) as number),
          ),
        ),
      )

      return icpResult
    },
  }), () => queryClient)

  const loadCoordinates = async (event: Event): Promise<Coordinate[] | undefined> => {
    const target = event.target as HTMLInputElement

    if (!target.files || target.files[0] === undefined) {
      console.debug('invalid file')
      return
    }

    const file = target.files[0]
    return parsePositioningFile(file)
  }

  const uploadPositionObjectMutation = createMutation(() => ({
    mutationFn: ({ event }: { event: Event }) => loadCoordinates(event),
  }), () => queryClient)

  return {
    uploadPositionObjectMutation,
    checkPositionsComplianceMutation,
  }
}

export default usePositioningAssistant
